import { createRootRoute, Outlet, redirect, useRouter } from '@tanstack/react-router';
import { Suspense } from 'react';
import { Compass } from 'lucide-react';
import { ForgeShell } from '../components/AppShell/ForgeShell';
import { FortressShell } from '../components/AppShell/FortressShell';
import { CommandPalette } from '../components/common/CommandPalette';
import { EmptyState, ErrorState, PageContainer, Skeleton } from '../components/ui';
import { useAuthStore } from '../store/useAuthStore';
import { useMyExperience } from '../hooks/useExperience';

// Routes that render without an authenticated session.
const PUBLIC_ROUTES = new Set(['/login', '/dev-login', '/forgot-password', '/auth/callback']);

// Role-gated route prefixes. Trainees can ONLY reach the trainee portal,
// the public landing, and their assigned lab. Direct URL navigation to
// instructor surfaces (scenarios catalog, all ranges, admin) bounces them
// back to /. Defense-in-depth: even if nav is hidden, typing the URL
// shouldn't leak data. Backend RBAC is the second line - handlers re-check
// the role via require_role() before returning data.
const INSTRUCTOR_ONLY_PREFIXES = [
  '/scenarios',
  '/ranges',
  '/range-health',
  '/monitoring',
  '/cost',
  '/blueprints',
  '/builder',
  '/admin',
];

/**
 * Root route — persistent shell with header, side-nav, and the page outlet.
 *
 * The page chrome is mounted once per session; only the `<Outlet />` is
 * swapped on navigation. Nav config lives in `components/AppShell/NavConfig.ts`
 * so adding a page is a one-line registration there + a `src/routes/<path>.tsx`
 * file (TanStack file-based routing).
 *
 * Routes are split by Vite at the file-route boundary, so the first render
 * doesn't pay for code in routes the user hasn't visited yet.
 */
export const Route = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
  // Without this, ANY render-time throw in a route fell through to TanStack's
  // unstyled default: a white pane with no shell, no theme and no way back.
  errorComponent: RouteError,
  beforeLoad: ({ location }) => {
    // Public routes bypass the auth gate (login, dev-login).
    if (PUBLIC_ROUTES.has(location.pathname)) {
      return;
    }
    // SSR-safe: Zustand persist is browser-only; treat lack of token as
    // "not authenticated" and bounce to /login. Subsequent navigation
    // re-runs beforeLoad — once the user signs in, isAuthenticated flips
    // and they can reach the requested path.
    const isAuthed = useAuthStore.getState().isAuthenticated;
    if (!isAuthed) {
      throw redirect({ to: '/login' });
    }
    // Trainee bounce: if a trainee hits an instructor-only prefix via a
    // typed URL or a stale bookmark, redirect to their mission portal.
    // Also bounce the bare landing (/) to /trainee so the post-login
    // experience drops them on something useful.
    const role = useAuthStore.getState().user?.role ?? null;
    if (role === 'trainee') {
      const path = location.pathname;
      const isInstructorOnly = INSTRUCTOR_ONLY_PREFIXES.some(
        (p) => path === p || path.startsWith(p + '/'),
      );
      if (isInstructorOnly || path === '/') {
        throw redirect({ to: '/trainee' });
      }
    }
  },
});

function RootLayout() {
  // Public routes (/login, /dev-login) skip the chrome so the user lands on
  // the centred sign-in card without the side-nav or header looking broken.
  const isAuthed = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.user?.role);
  // Trainees whose org runs the Citadel template get the dark fortress chrome on
  // EVERY page (so opening a lab never drops them into the default UI). Others
  // get the standard SaaS shell. Experience is cached (5m) from the /trainee
  // load, so this resolves before the trainee clicks into a lab.
  const experience = useMyExperience();
  const isFortress = role === 'trainee' && experience.data === 'fortress';

  if (!isAuthed) {
    return (
      <main className="min-h-screen bg-void">
        <Suspense fallback={<PageFallback />}>
          <Outlet />
        </Suspense>
      </main>
    );
  }

  const Shell = isFortress ? FortressShell : ForgeShell;
  // Authenticated chrome — mounted once per session; only <Outlet /> swaps on nav.
  return (
    <Shell>
      <Suspense fallback={<PageFallback />}>
        <Outlet />
      </Suspense>
      <CommandPalette />
    </Shell>
  );
}

function PageFallback() {
  return (
    <div style={{ padding: 24 }}>
      <Skeleton lines={3} height={16} gap={12} />
    </div>
  );
}

function NotFound() {
  const router = useRouter();
  return (
    <PageContainer size="narrow">
      <EmptyState
        illustration={<Compass size={48} />}
        title="404 — page not found"
        description="The page you tried to reach is not registered in the router. It may have moved, or the link may be out of date."
        cta={{
          label: 'Back to dashboard',
          onClick: () => void router.navigate({ to: '/' }),
        }}
      />
    </PageContainer>
  );
}

function RouteError({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <PageContainer size="narrow">
      <ErrorState title="This page hit an error" error={error} onRetry={reset} />
    </PageContainer>
  );
}
